import { RenderObject } from '../object/RenderObject';
import { AnimationState, StateDefinition } from './AnimationState';

/*
 * Hardcoded fallback events for unmigrated models. The asset-manager animation
 * viewer now authors per-clip events stored in `_ANIMATION_EVENTS` skin extras
 * (see `AnimationEventsExtras.ts`); when present those win in
 * `AnimationState.dispatchEvents`. These defaults still fire for models that
 * haven't been touched in the editor, so existing footstep behaviour keeps
 * working without an explicit migration step.
 */
const ENTITY_STATE_BEHAVIORS: StateDefinition = {
  Idle: { repeat: 'always', speed: 0.5 },
  Walk: {
    repeat: 'always',
    speed: 1.0,
    events: [
      { at: 0.25, name: 'foot_down' },
      { at: 0.75, name: 'foot_down' },
    ],
  },
  Run: {
    repeat: 'always',
    speed: 1.0,
    events: [
      { at: 0.2, name: 'foot_down' },
      { at: 0.7, name: 'foot_down' },
    ],
  },
  Death: { repeat: 'once', speed: 0.3 },
  Attack: { repeat: 'once', nextState: 'Idle', speed: 0.4 },
};

export class EntityAnimationState extends AnimationState {
  private stateToClips: Record<string, string[]>;
  private activeClips: Record<string, string> = {};

  constructor(object: RenderObject, stateMapping: Record<string, string[]>) {
    super(object);
    this.stateToClips = stateMapping;

    // Build allowedStates from the mapping, using known entity behaviors
    const allowed: StateDefinition = {};
    for (const [stateName, clips] of Object.entries(stateMapping)) {
      if (clips.length > 0) {
        const behavior = ENTITY_STATE_BEHAVIORS[stateName];
        if (behavior) {
          allowed[stateName] = { ...behavior };
        } else {
          // Unknown entity state — default to looping
          allowed[stateName] = { repeat: 'always', speed: 1.0 };
        }
        this.activeClips[stateName] = clips[0];
      }
    }
    this.allowedStates = allowed;
  }

  public override switchState(newState: string) {
    // Only pick a random clip when actually changing state, otherwise
    // repeated calls with the same state would swap clips mid-animation
    if (newState !== this.currentState) {
      const clips = this.stateToClips[newState];
      if (clips && clips.length > 1) {
        this.activeClips[newState] = clips[Math.floor(Math.random() * clips.length)];
      }
    }
    super.switchState(newState);
  }

  protected override resolveClipId(stateName: string): string {
    return this.activeClips[stateName] ?? stateName;
  }
}
